import { useEffect, useMemo, useState } from "react";
import { estadoDoFeriadoNaLoja, formatDataBR, listFeriados, rotuloCompetencia, type Feriado } from "@/api/escala.service";
import { colunasDoMes, type DiaColuna } from "@/components/escala/espelho";

// Número com vírgula, no máximo uma casa decimal.
export const formatNumero = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });

export const formatPercentual = (n: number, casas = 0) =>
  n.toLocaleString("pt-BR", { style: "percent", minimumFractionDigits: casas, maximumFractionDigits: casas });

// "1 colaborador" / "3 colaboradores".
export const plural = (n: number, singular: string, pluralForma = `${singular}s`) =>
  `${n.toLocaleString("pt-BR")} ${n === 1 ? singular : pluralForma}`;

// Quantos valores diferentes a chave assume.
export const distintos = <T>(itens: T[], chave: (item: T) => string | undefined) =>
  new Set(itens.map(chave).filter(Boolean)).size;

export function contarPor<T>(itens: T[], chave: (item: T) => string): Map<string, number> {
  const contagem = new Map<string, number>();
  for (const item of itens) contagem.set(chave(item), (contagem.get(chave(item)) ?? 0) + 1);
  return contagem;
}

// A chave que mais aparece e quantas vezes; empate fica com a primeira.
export function maisFrequente<T>(itens: T[], chave: (item: T) => string): [string, number] | undefined {
  return [...contarPor(itens, chave).entries()].reduce<[string, number] | undefined>((p, c) => (!p || c[1] > p[1] ? c : p), undefined);
}

// "04/10/2026 (dom)" no domingo; a data pura nos outros dias.
export const rotuloData = (iso: string) => `${formatDataBR(iso)}${new Date(`${iso}T00:00:00`).getDay() === 0 ? " (dom)" : ""}`;

// Mensagem de relatório vazio, igual em todas as abas.
export const semResultado = (ano?: number, mes?: number) =>
  ano && mes
    ? `Nenhum resultado em ${rotuloCompetencia(ano, mes)} para os filtros escolhidos.`
    : "Nenhum resultado para os filtros escolhidos.";

// Colunas do mês com os feriados do client marcados como valem na loja do
// filtro (a marcação dela, ou o padrão do feriado); sem filtro de loja, pelo
// padrão. O que não é feriado ali não marca. Sem feriado, a grade sai igual.
export function useColunasDoMes(ano: number, mes: number, lojaId: string | undefined, clientId: string): DiaColuna[] {
  const [feriados, setFeriados] = useState<Feriado[]>([]);

  useEffect(() => {
    let cancelado = false;
    listFeriados(ano, clientId)
      .then((lista) => !cancelado && setFeriados(lista ?? []))
      .catch(() => !cancelado && setFeriados([]));
    return () => {
      cancelado = true;
    };
  }, [ano, clientId]);

  return useMemo(() => {
    const prefixo = `${ano}-${String(mes).padStart(2, "0")}-`;
    const doMes = feriados.flatMap((f) => {
      if (!f.ativo || !f.data.startsWith(prefixo)) return [];
      const estado = lojaId ? estadoDoFeriadoNaLoja(f, lojaId) : (f.padrao ?? "FECHA");
      return estado === "NAO_E_FERIADO" ? [] : [{ data: f.data, nome: f.nome, fecha_loja: estado === "FECHA" }];
    });
    return colunasDoMes(ano, mes, doMes);
  }, [feriados, ano, mes, lojaId]);
}
